import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgComponentOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonComponent, DropdownListComponent, MultiselectItem } from '@ui/aos-lib';
import { filter, map, startWith, Subscription } from 'rxjs';

import { ReportGroupsDynamicFilterService } from './report-groups-dynamic-filter.service';
import { ReportGroupsDynamicFilter } from './report-groups-dynamic-filter.types';
import { ReportGroupsFilterForm } from '../report-groups-filter-criteria.types';

@Component({
  selector: 'aos-report-groups-dynamic-filter',
  imports: [ReactiveFormsModule, ButtonComponent, DropdownListComponent, NgComponentOutlet],
  template: `
    <form
      class="aos-report-groups-dynamic-filter"
      [formGroup]="form()"
    >
      <ui-dropdown-list
        class="aos-report-groups-dynamic-filter__filter-dropdown"
        [items]="items()"
        [default]="defaultValue()"
        [selected]="selectedNameFilter()"
        [isGrouped]="true"
        [formControlName]="formCtrlName"
      ></ui-dropdown-list>

      @if (filter?.component) {
        <ng-container *ngComponentOutlet="filter!.component; inputs: getInputs(filter!)"> </ng-container>
      }

      @if (removeButtonDisabled()) {
        <div class="aos-report-groups-dynamic-filter__tooltip-container">
          <span class="aos-report-groups-dynamic-filter__tooltip">
            {{ removeButtonTooltip() }}
          </span>
          <ui-button
            class="aos-report-groups-dynamic-filter__remove-button"
            [iconName]="deleteButtonIconName"
            [size]="deleteButtonIconSize"
            [disabled]="true"
          ></ui-button>
        </div>
      } @else {
        <ui-button
          class="aos-report-groups-dynamic-filter__remove-button"
          [iconName]="deleteButtonIconName"
          [size]="deleteButtonIconSize"
          (click)="remove.emit()"
        ></ui-button>
      }
    </form>
  `,
  styleUrl: './report-groups-dynamic-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportGroupsDynamicFilterComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly reportGroupsDynamicFilterService = inject(ReportGroupsDynamicFilterService);

  items = input<MultiselectItem[]>([]);
  defaultValue = input<MultiselectItem>(this.items()[0]);
  remove = output<void>();
  form = input.required<FormGroup>();

  protected readonly selectedNameFilter = computed(
    () => this.form().get(this.formCtrlName)?.value ?? this.defaultValue()
  );

  private readonly formChange = effect(() => {
    const form = this.form();
    if (!form) {
      return;
    }

    if (form.controls.name && form.controls.operator && form.controls.values && !this.filter) {
      const nameFilterValue: MultiselectItem & { fieldName: string } = form.controls.name?.value as MultiselectItem & {
        fieldName: string;
      };
      this.filter = this.reportGroupsDynamicFilterService.findFilter(nameFilterValue.fieldName);

      if (!this.filter) {
        return;
      }

      form.controls.name.setValue(nameFilterValue);
      this.filter.controls.forEach(getCtrl => {
        const { name, overrideDropdownListSelected } = getCtrl();
        const ctrl = form.controls[name];
        form.controls[name].setValue(ctrl.value);

        // todo: remove when DropdownListComponent.selected is fixed
        if (overrideDropdownListSelected?.enabled) {
          this.filter = {
            ...this.filter!,
            inputs: {
              ...this.filter!.inputs,
              [overrideDropdownListSelected.inputName]: ctrl.value
            }
          };
        }
      });
    }

    const nameFormControl = form.get(this.formCtrlName)!;
    nameFormControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: MultiselectItem & { fieldName: string }) => {
        this.updateFilterForm(form, value);
      });

    if (this.formValueChangeSubscription) {
      this.formValueChangeSubscription.unsubscribe();
      this.formValueChangeSubscription = null;
    }

    const currentFilterName: string = nameFormControl.value.value;
    this.formValueChangeSubscription = form
      .parent!.valueChanges.pipe(
        startWith(form.parent!.value),
        map((values: ReportGroupsFilterForm[]) => {
          return values.find(value => ((value.name as MultiselectItem)?.value as string) === currentFilterName);
        }),
        filter((value): value is ReportGroupsFilterForm => !!value?.options),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(v => {
        this.removeButtonTooltip.set(v.options?.removeButtonTooltip as string);
        this.removeButtonDisabled.set(!!v.options?.removeButtonDisabled);
      })!;
  });

  protected removeButtonTooltip = signal('');
  protected removeButtonDisabled = signal(false);
  protected filter: ReportGroupsDynamicFilter | null = null;
  protected readonly formCtrlName = 'name';
  protected readonly formCtrlOptions = 'options';

  private formValueChangeSubscription: Subscription | null = null;

  protected getInputs(filter: ReportGroupsDynamicFilter): ReportGroupsDynamicFilter['inputs'] {
    return {
      ...filter.inputs,
      form: this.form()
    };
  }

  protected readonly deleteButtonIconName = 'delete';
  protected readonly deleteButtonIconSize = 'small';

  private updateFilterForm(form: FormGroup, nameFormControlValue: MultiselectItem & { fieldName: string }): void {
    const filterName: string | null = nameFormControlValue?.fieldName as string | null;
    this.filter = this.reportGroupsDynamicFilterService.findFilter(filterName);
    if (!this.filter) {
      return;
    }

    this.filter.controls.forEach(getCtrl => {
      const { name, ctrl } = getCtrl();
      form.setControl(name, ctrl, {
        emitEvent: false
      });
    });

    form.updateValueAndValidity({ emitEvent: false });
  }
}
